import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { useBookmarks } from '../contexts/BookmarkContext';
import { HelperProfile, HelperSearchRequest } from '../types';
import { apiService } from '../services/api';
import { Loading } from '../components';
import { formatLocalDate } from '../utils/date';
import HelperCard from '../components/HelperCard';
import { isHelperViewed } from '../utils/viewedStore';
import { useDataDictionaries, DATA_DICTIONARY_TYPES } from '../hooks/useDataDictionary';
import { getDictDisplayValue } from '../utils/i18n';
import { Search, Users, SlidersHorizontal, X, ArrowUpDown, CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';

const toSel = (v: string, empty = 'any') => v || empty;
const fromSel = (v: string, empty = 'any') => v === empty ? '' : v;

const SearchCandidatesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const { showAlert } = useAlert();
  const { isBookmarked, toggleBookmark } = useBookmarks();
  const [helpers, setHelpers] = useState<HelperProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [location, setLocation] = useState('');
  const [selectedNationalities, setSelectedNationalities] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 9;
  const [gender, setGender] = useState<'' | 'MALE' | 'FEMALE'>('');
  const [educationLevel, setEducationLevel] = useState<string>('');
  const [minSalary, setMinSalary] = useState<string>('');
  const [maxSalary, setMaxSalary] = useState<string>('');
  const [selectedLanguageSkills, setSelectedLanguageSkills] = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [minExperienceYears, setMinExperienceYears] = useState<string>('');
  const [availableStartDateFrom, setAvailableStartDateFrom] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');
  const [helperCategory, setHelperCategory] = useState<'' | 'LOCAL_COMPLETED' | 'OVERSEAS_NEW' | 'OVERSEAS_HELPER'>('');

  // 使用數據字典
  const { dictionariesByParent } = useDataDictionaries({
    parentCodes: [
      DATA_DICTIONARY_TYPES.NATIONALITY,
      DATA_DICTIONARY_TYPES.GENDER,
      DATA_DICTIONARY_TYPES.EDUCATION_LEVEL,
      DATA_DICTIONARY_TYPES.LANGUAGE_SKILLS,
      DATA_DICTIONARY_TYPES.RESPONSIBILITIES_CARE,
      DATA_DICTIONARY_TYPES.HOUSEWORK,
    ],
  });
  const nationalityDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.NATIONALITY) ?? [];
  const genderDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.GENDER) ?? [];
  const educationDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.EDUCATION_LEVEL) ?? [];
  const languageSkillDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.LANGUAGE_SKILLS) ?? [];
  const careDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.RESPONSIBILITIES_CARE) ?? [];
  const houseworkDicts = dictionariesByParent.get(DATA_DICTIONARY_TYPES.HOUSEWORK) ?? [];
  const skillDicts = [...careDicts, ...houseworkDicts];

  const appliedKeyword = useRef('');

  const loadHelpers = useCallback(async (pageNum: number = 0, overrides?: {
    location?: string;
    nationalities?: string[];
    gender?: string;
    educationLevel?: string;
    minSalary?: string;
    maxSalary?: string;
    languageSkills?: string[];
    skills?: string[];
    minExperienceYears?: string;
    availableStartDateFrom?: string;
    sortBy?: string;
    sortDirection?: 'ASC' | 'DESC';
    helperCategory?: '' | 'LOCAL_COMPLETED' | 'OVERSEAS_NEW' | 'OVERSEAS_HELPER';
  }) => {
    const k = appliedKeyword.current;
    const loc = overrides?.location ?? location;
    const nats = overrides?.nationalities ?? selectedNationalities;
    const gen = overrides?.gender ?? gender;
    const edu = overrides?.educationLevel ?? educationLevel;
    const minSal = overrides?.minSalary ?? minSalary;
    const maxSal = overrides?.maxSalary ?? maxSalary;
    const langSkillsCodes = overrides?.languageSkills ?? selectedLanguageSkills;
    const skillCodes = overrides?.skills ?? selectedSkills;
    const minExp = overrides?.minExperienceYears ?? minExperienceYears;
    const availFrom = overrides?.availableStartDateFrom ?? availableStartDateFrom;
    const sortB = overrides?.sortBy ?? sortBy;
    const sortD = overrides?.sortDirection ?? sortDirection;
    const cat = overrides?.helperCategory ?? helperCategory;
    try {
      setLoading(true);
      setHelpers([]);
      const searchRequest: HelperSearchRequest = {
        page: pageNum,
        size: pageSize,
        sortBy: sortB,
        sortDirection: sortD,

      };
      if (k.trim()) searchRequest.keyword = k.trim();
      if (loc.trim() && loc !== 'all') searchRequest.currentLocation = loc.trim();
      if (nats.length > 0) {
        searchRequest.nationality = nats.join(',');
      }
      if (gen) searchRequest.gender = gen as 'MALE' | 'FEMALE';
      if (edu) searchRequest.educationLevel = edu as HelperSearchRequest['educationLevel'];
      if (minSal) searchRequest.minSalary = Number(minSal);
      if (maxSal) searchRequest.maxSalary = Number(maxSal);
      const langSkillsStr = langSkillsCodes.join(',');
      if (langSkillsStr) searchRequest.languageSkills = langSkillsStr;
      const skillsStr = skillCodes.join(',');
      if (skillsStr) searchRequest.skills = skillsStr;
      if (minExp) searchRequest.minExperienceYears = Number(minExp);
      if (availFrom) searchRequest.availableStartDateFrom = availFrom;
      if (cat) searchRequest.helperCategory = cat as HelperSearchRequest['helperCategory'];

      const response = await apiService.helpers.search(searchRequest);
      if (response.success && response.data) {
        const { content, totalPages: pages, totalElements } = response.data;
        const formatted = (content || []).map((item: HelperProfile & { expectedSalary?: unknown }) => {
          if (item.expectedSalary != null && typeof item.expectedSalary === 'string') {
            (item as HelperProfile).expectedSalary = parseFloat(item.expectedSalary) as number;
          }
          return item as HelperProfile;
        });
        setHelpers(formatted);
        setTotalPages(pages ?? 1);
        setTotalCount(totalElements ?? formatted.length);
        setPage(pageNum);
      } else {
        setHelpers([]);
      }
    } catch (e: unknown) {
      setHelpers([]);
      showAlert({ type: 'error', message: t('candidates.loadFailed') });
    } finally {
      setLoading(false);
    }
  }, [user?.role, location, selectedNationalities, gender, educationLevel, minSalary, maxSalary, selectedLanguageSkills, selectedSkills, minExperienceYears, availableStartDateFrom, sortBy, sortDirection, helperCategory, showAlert, t]);

  useEffect(() => {
    loadHelpers(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    const timer = setTimeout(() => loadHelpers(0), 400);
    return () => clearTimeout(timer);
  }, [loadHelpers]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!authLoading && user?.role === 'HELPER') {
    return <Navigate to="/job-postings" replace />;
  }

  const handleSearch = () => { appliedKeyword.current = keyword; loadHelpers(0); };
  const handleReset = () => {
    setKeyword('');
    setLocation('');
    setSelectedNationalities([]);
    setGender('');
    setEducationLevel('');
    setMinSalary('');
    setMaxSalary('');
    setSelectedLanguageSkills([]);
    setSelectedSkills([]);
    setMinExperienceYears('');
    setAvailableStartDateFrom('');
    setSortBy('createdAt');
    setSortDirection('DESC');
    setHelperCategory('');
    setPage(0);
  };

  const toggleNationality = (nationalityCode: string) => {
    setSelectedNationalities(prev => 
      prev.includes(nationalityCode)
        ? prev.filter(n => n !== nationalityCode)
        : [...prev, nationalityCode]
    );
  };

  const activeFilterCount = [
    location !== '',
    selectedNationalities.length > 0,
    gender !== '',
    educationLevel !== '',
    minSalary !== '',
    maxSalary !== '',
    selectedLanguageSkills.length > 0,
    selectedSkills.length > 0,
    minExperienceYears !== '',
    availableStartDateFrom !== '',
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-primary py-12 md:py-16">
          <div className="container mx-auto px-4">
            <h1 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4 text-center">
              {t('candidates.searchTitle', '尋找合適的外傭')}
            </h1>
            <p className="text-primary-foreground/80 text-center mb-8 max-w-2xl mx-auto">
              {t('candidates.searchSubtitle')}
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder={t('candidates.searchByName')}
                  className="pl-12 h-12 bg-background text-foreground border-none"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <Button className="h-12 px-6 shrink-0" onClick={handleSearch}>
                {t('common.search', '搜尋')}
              </Button>
            </div>

            {/* Quick Filter Tabs */}
            <div className="flex justify-center gap-2 mt-4">
              {(['', 'LOCAL_COMPLETED', 'OVERSEAS_NEW', 'OVERSEAS_HELPER'] as const).map((cat) => {
                const labels: Record<string, string> = {
                  '': t('candidates.catAll', '全部'),
                  LOCAL_COMPLETED: t('candidates.catLocal', '本地完約'),
                  OVERSEAS_NEW: t('candidates.catOverseasNew', '海外新人'),
                  OVERSEAS_HELPER: t('candidates.catOverseas', '海外傭工'),
                };
                return (
                  <button
                    key={cat}
                    onClick={() => setHelperCategory(cat)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      helperCategory === cat
                        ? 'bg-primary-foreground text-primary'
                        : 'bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30'
                    }`}
                  >
                    {labels[cat]}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Filters & Results */}
        <section className="py-8">
          <div className="container mx-auto px-4">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Left Sidebar - Filters */}
              <aside className="hidden lg:block w-72 flex-shrink-0">
                <div className="sticky top-24 bg-card rounded-xl border p-6 space-y-6">
                  <div className="flex items-center justify-between">
                    <h2 className="font-semibold text-lg">{t('candidates.filters')}</h2>
                    {activeFilterCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={handleReset} className="h-8 px-2 text-xs">
                        {t('candidates.clearFilters')}
                      </Button>
                    )}
                  </div>

                  {/* Location Filter */}
                  <div className="space-y-3">
                    <Label htmlFor="location" className="text-sm font-medium">{t('candidates.location')}</Label>
                    <Select
                      value={toSel(location, 'all')}
                      onValueChange={(val) => setLocation(fromSel(val, 'all'))}
                    >
                      <SelectTrigger id="location" className="w-full bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('candidates.anyLocation')}</SelectItem>
                        <SelectItem value="Hong Kong">{t('candidates.locationHK', '香港')}</SelectItem>
                        {nationalityDicts.map(dict => (
                          <SelectItem key={dict.code} value={dict.valueEnglish}>
                            {getDictDisplayValue(dict)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Nationality Filter */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">{t('common.nationality')}</Label>
                    <div className="grid grid-cols-2 gap-x-2 gap-y-2">
                      {nationalityDicts.map(dict => (
                        <div key={dict.code} className="flex items-center gap-2">
                          <Checkbox
                            id={`nat-${dict.code}`}
                            checked={selectedNationalities.includes(dict.code)}
                            onCheckedChange={() => toggleNationality(dict.code)}
                          />
                          <Label htmlFor={`nat-${dict.code}`} className="text-sm cursor-pointer font-normal">
                            {getDictDisplayValue(dict)}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Gender Filter */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">{t('common.gender')}</Label>
                    <Select value={toSel(gender)} onValueChange={(val) => setGender(fromSel(val) as '' | 'MALE' | 'FEMALE')}>
                      <SelectTrigger className="w-full bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">{t('candidates.genderAny', '不限')}</SelectItem>
                        {genderDicts.map(d => (
                          <SelectItem key={d.code} value={d.code}>{getDictDisplayValue(d)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Education Filter */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">{t('candidates.education', '教育程度')}</Label>
                    <Select value={toSel(educationLevel)} onValueChange={(val) => setEducationLevel(fromSel(val))}>
                      <SelectTrigger className="w-full bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">{t('candidates.educationAny', '不限')}</SelectItem>
                        {educationDicts.map(d => (
                          <SelectItem key={d.code} value={d.code}>{getDictDisplayValue(d)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Salary Range Filter */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">{t('candidates.salaryRange', '薪金範圍 (HKD)')}</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder={t('candidates.minSalary', '最低')}
                        value={minSalary}
                        onChange={(e) => setMinSalary(e.target.value)}
                        className="bg-background"
                        min="0"
                      />
                      <Input
                        type="number"
                        placeholder={t('candidates.maxSalary', '最高')}
                        value={maxSalary}
                        onChange={(e) => setMaxSalary(e.target.value)}
                        className="bg-background"
                        min="0"
                      />
                    </div>
                  </div>

                  {/* Languages Filter */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">{t('candidates.languageSkills', '語言能力')}</Label>
                    <div className="grid grid-cols-2 gap-x-2 gap-y-2">
                      {languageSkillDicts.map(dict => (
                        <div key={dict.code} className="flex items-center gap-2">
                          <Checkbox
                            id={`lang-${dict.code}`}
                            checked={selectedLanguageSkills.includes(dict.code)}
                            onCheckedChange={(checked) => {
                              if (checked) setSelectedLanguageSkills([...selectedLanguageSkills, dict.code]);
                              else setSelectedLanguageSkills(selectedLanguageSkills.filter(c => c !== dict.code));
                            }}
                          />
                          <Label htmlFor={`lang-${dict.code}`} className="text-sm cursor-pointer font-normal">
                            {getDictDisplayValue(dict)}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Skills Filter */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">{t('common.skills')}</Label>
                    <div className="grid grid-cols-2 gap-x-2 gap-y-2">
                      {skillDicts.map(dict => (
                        <div key={dict.code} className="flex items-center gap-2">
                          <Checkbox
                            id={`skill-${dict.code}`}
                            checked={selectedSkills.includes(dict.code)}
                            onCheckedChange={(checked) => {
                              if (checked) setSelectedSkills([...selectedSkills, dict.code]);
                              else setSelectedSkills(selectedSkills.filter(c => c !== dict.code));
                            }}
                          />
                          <Label htmlFor={`skill-${dict.code}`} className="text-sm cursor-pointer font-normal">
                            {getDictDisplayValue(dict)}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Min Experience Filter */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">{t('candidates.minExperienceYears', '最少工作經驗 (年)')}</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={minExperienceYears}
                      onChange={(e) => setMinExperienceYears(e.target.value)}
                      className="bg-background"
                      min="0"
                    />
                  </div>

                  {/* Available From Filter */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">{t('candidates.availableFrom', '可開始日期')}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal bg-background">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {availableStartDateFrom
                            ? new Intl.DateTimeFormat(i18n.language === 'zh-TW' ? 'zh-TW' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(availableStartDateFrom))
                            : <span className="text-muted-foreground">{t('common.selectDate', '選擇日期')}</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={availableStartDateFrom ? new Date(availableStartDateFrom) : undefined}
                          onSelect={(d) => setAvailableStartDateFrom(d ? formatLocalDate(d) : '')}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                </div>
              </aside>

              {/* Main Content */}
              <div className="flex-1 min-w-0">
                {/* Mobile Filter Button */}
                <div className="lg:hidden mb-4">
                  <Button
                    variant="outline"
                    className="w-full gap-2"
                    onClick={() => {
                      const sidebar = document.getElementById('mobile-filters');
                      if (sidebar) {
                        sidebar.classList.toggle('hidden');
                      }
                    }}
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                    {t('candidates.filters')}
                    {activeFilterCount > 0 && (
                      <Badge variant="secondary" className="ml-1">
                        {activeFilterCount}
                      </Badge>
                    )}
                  </Button>
                </div>

                {/* Mobile Filters (collapsible) */}
                <div id="mobile-filters" className="hidden lg:hidden mb-6 bg-card rounded-xl border p-4 space-y-4">
                  {/* Location Filter */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t('candidates.location')}</Label>
                    <Select
                      value={toSel(location, 'all')}
                      onValueChange={(val) => setLocation(fromSel(val, 'all'))}
                    >
                      <SelectTrigger className="w-full bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('candidates.anyLocation')}</SelectItem>
                        <SelectItem value="Hong Kong">{t('candidates.locationHK', '香港')}</SelectItem>
                        {nationalityDicts.map(dict => (
                          <SelectItem key={dict.code} value={dict.valueEnglish}>
                            {getDictDisplayValue(dict)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Nationality */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t('common.nationality')}</Label>
                    <div className="flex flex-wrap gap-2">
                      {nationalityDicts.map((dict) => (
                        <label key={dict.code} className="flex items-center gap-1.5 text-sm font-normal">
                          <Checkbox
                            checked={selectedNationalities.includes(dict.code)}
                            onCheckedChange={() => toggleNationality(dict.code)}
                          />
                          {getDictDisplayValue(dict)}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Gender Filter */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t('common.gender')}</Label>
                    <Select value={toSel(gender)} onValueChange={(val) => setGender(fromSel(val) as '' | 'MALE' | 'FEMALE')}>
                      <SelectTrigger className="w-full bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">{t('candidates.genderAny', '不限')}</SelectItem>
                        {genderDicts.map(d => (
                          <SelectItem key={d.code} value={d.code}>{getDictDisplayValue(d)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Education Filter */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t('candidates.education', '教育程度')}</Label>
                    <Select value={toSel(educationLevel)} onValueChange={(val) => setEducationLevel(fromSel(val))}>
                      <SelectTrigger className="w-full bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">{t('candidates.educationAny', '不限')}</SelectItem>
                        {educationDicts.map(d => (
                          <SelectItem key={d.code} value={d.code}>{getDictDisplayValue(d)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Salary Range Filter */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t('candidates.salaryRange', '薪金範圍 (HKD)')}</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder={t('candidates.minSalary', '最低')}
                        value={minSalary}
                        onChange={(e) => setMinSalary(e.target.value)}
                        className="bg-background"
                        min="0"
                      />
                      <Input
                        type="number"
                        placeholder={t('candidates.maxSalary', '最高')}
                        value={maxSalary}
                        onChange={(e) => setMaxSalary(e.target.value)}
                        className="bg-background"
                        min="0"
                      />
                    </div>
                  </div>

                  {/* Languages Filter */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t('candidates.languageSkills', '語言能力')}</Label>
                    <div className="flex flex-wrap gap-2">
                      {languageSkillDicts.map(dict => (
                        <label key={dict.code} className="flex items-center gap-1.5 text-sm font-normal">
                          <Checkbox
                            checked={selectedLanguageSkills.includes(dict.code)}
                            onCheckedChange={(checked) => {
                              if (checked) setSelectedLanguageSkills([...selectedLanguageSkills, dict.code]);
                              else setSelectedLanguageSkills(selectedLanguageSkills.filter(c => c !== dict.code));
                            }}
                          />
                          {getDictDisplayValue(dict)}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Skills Filter */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t('common.skills')}</Label>
                    <div className="flex flex-wrap gap-2">
                      {skillDicts.map(dict => (
                        <label key={dict.code} className="flex items-center gap-1.5 text-sm font-normal">
                          <Checkbox
                            checked={selectedSkills.includes(dict.code)}
                            onCheckedChange={(checked) => {
                              if (checked) setSelectedSkills([...selectedSkills, dict.code]);
                              else setSelectedSkills(selectedSkills.filter(c => c !== dict.code));
                            }}
                          />
                          {getDictDisplayValue(dict)}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Min Experience Filter */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t('candidates.minExperienceYears', '最少工作經驗 (年)')}</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={minExperienceYears}
                      onChange={(e) => setMinExperienceYears(e.target.value)}
                      className="bg-background"
                      min="0"
                    />
                  </div>

                  {/* Available From Filter */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t('candidates.availableFrom', '可開始日期')}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal bg-background">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {availableStartDateFrom
                            ? new Intl.DateTimeFormat(i18n.language === 'zh-TW' ? 'zh-TW' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(availableStartDateFrom))
                            : <span className="text-muted-foreground">{t('common.selectDate', '選擇日期')}</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={availableStartDateFrom ? new Date(availableStartDateFrom) : undefined}
                          onSelect={(d) => setAvailableStartDateFrom(d ? formatLocalDate(d) : '')}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {activeFilterCount > 0 && (
                    <Button variant="outline" size="sm" onClick={handleReset} className="w-full">
                      <X className="h-4 w-4 mr-1" />
                      {t('candidates.clearFilters')}
                    </Button>
                  )}
                </div>

                {/* Sort & Count Bar */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
                  <span className="text-sm font-medium text-foreground">
                    {totalCount > 0
                      ? t('candidates.foundHelpers', { count: totalCount })
                      : t('candidates.noHelpersFound', '沒有找到符合條件的外傭')}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">{t('searchCandidates.sort.label')}</span>
                    <Select value={sortBy} onValueChange={(val) => setSortBy(val)}>
                      <SelectTrigger className="w-[180px] bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="createdAt">{t('searchCandidates.sort.createdAt')}</SelectItem>
                        <SelectItem value="experienceYears">{t('searchCandidates.sort.experienceYears')}</SelectItem>
                        <SelectItem value="age">{t('searchCandidates.sort.age')}</SelectItem>
                        <SelectItem value="availableStartDate">{t('searchCandidates.sort.availableStartDate')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      onClick={() => setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC')}
                      className="p-2 rounded hover:bg-muted transition-colors"
                      title={sortDirection === 'ASC' ? t('searchCandidates.sort.direction.asc') : t('searchCandidates.sort.direction.desc')}
                    >
                      <ArrowUpDown className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Results Grid */}
                {loading ? (
                  <div className="min-h-[400px] flex items-center justify-center">
                    <Loading message={t('candidates.loading')} />
                  </div>
                ) : helpers.length === 0 ? (
                  <Card className="min-h-[300px] flex flex-col items-center justify-center text-center p-8 border-dashed bg-muted/10">
                    <CardContent className="flex flex-col items-center justify-center pt-6">
                      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-6">
                        <Users className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <h3 className="text-xl font-semibold text-foreground mb-2">{t('candidates.noHelpersFound')}</h3>
                      <p className="text-muted-foreground mb-8 max-w-sm">
                        {t('candidates.noHelpersDesc')}
                      </p>
                      <Button variant="outline" onClick={handleReset}>{t('candidates.clearFilters')}</Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-10">
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                      {helpers.map((h) => (
                        <HelperCard
                          key={h.id}
                          helper={h}
                          onViewDetail={(helper) => navigate(`/helper/${helper.id}`)}
                          onBookmark={(helper) => toggleBookmark(helper)}
                          isBookmarked={isBookmarked(h.id)}
                          isViewed={isHelperViewed(h.id)}
                        />
                      ))}
                    </div>

                    {totalPages > 1 && (
                      <div className="flex items-center justify-center gap-2 pt-6 pb-12">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => loadHelpers(page - 1)}
                          disabled={loading || page === 0}
                        >
                          {t('common.previous', '上一頁')}
                        </Button>
                        {Array.from({ length: totalPages }, (_, i) => {
                          if (
                            totalPages <= 7 ||
                            i === 0 ||
                            i === totalPages - 1 ||
                            (i >= page - 1 && i <= page + 1)
                          ) {
                            return (
                              <Button
                                key={i}
                                variant={i === page ? 'default' : 'outline'}
                                size="sm"
                                className="min-w-[36px]"
                                onClick={() => loadHelpers(i)}
                                disabled={loading}
                              >
                                {i + 1}
                              </Button>
                            );
                          }
                          if (i === page - 2 || i === page + 2) {
                            return <span key={i} className="px-1 text-muted-foreground">…</span>;
                          }
                          return null;
                        })}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => loadHelpers(page + 1)}
                          disabled={loading || page >= totalPages - 1}
                        >
                          {t('common.next', '下一頁')}
                        </Button>
                        <span className="ml-2 text-xs text-muted-foreground">
                          {t('common.totalCount', { count: totalCount, defaultValue: `共 ${totalCount} 位` })}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SearchCandidatesPage;
